import { Navigate } from 'react-router-dom';
import {
  ApiError,
  BuyerTeamEditor,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  errorMessage,
} from '@dataroom/shared';
import {
  useAddTeamMember,
  useBuyerRoomAccess,
  useMyTeam,
  useRemoveTeamMember,
  useSetTeamRoles,
  useWithdrawTeamRequest,
} from '@/api/hooks';

/**
 * "My team" (migrations 0070 + 0075): the lead buyer asks to add the people who
 * work this deal with them — colleagues and outside advisers of any email domain
 * — and the intermediary approves each one; the buyer removes members directly.
 * Each person is invited on approval, signs the NDA, and then sees what the
 * buyer sees; if the buyer's LOI is accepted, the team keeps access while other
 * buyers are paused.
 * The buyer only ever sees their own team.
 */
export function MyTeamPage() {
  const access = useBuyerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 text-sm text-muted-foreground md:px-6">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <MyTeamInner roomId={access.roomId} />;
}

function MyTeamInner({ roomId }: { roomId: string }) {
  const team = useMyTeam(roomId);
  const add = useAddTeamMember(roomId);
  const remove = useRemoveTeamMember(roomId);
  const withdraw = useWithdrawTeamRequest(roomId);
  const setRoles = useSetTeamRoles(roomId);

  if (team.error instanceof ApiError && team.error.status === 401) {
    return <Navigate to="/login" replace />;
  }
  // No team page in this room (a buyer-run room, or no buyer access).
  if (team.error instanceof ApiError && team.error.status === 404) {
    return <Navigate to={`/rooms/${roomId}`} replace />;
  }

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6">
      <Card>
        <CardHeader>
          <CardTitle>
            My team <HelpTip id="buyer.team.page" className="ml-1" />
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            The people working on this deal with you. Only you and the intermediary can see this
            list — other buyers and the sellers never do.
          </p>
        </CardHeader>
        <CardContent>
          {team.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : team.error ? (
            <p className="text-sm text-destructive">{errorMessage(team.error)}</p>
          ) : team.data ? (
            <div className="space-y-3">
              {team.data.reason ? (
                <p className="rounded-md border bg-surface-2 p-3 text-sm">{team.data.reason}</p>
              ) : null}
              {team.data.memberOfTeam ? null : (
                <BuyerTeamEditor
                  members={team.data.members}
                  requests={team.data.requests}
                  viewer="buyer"
                  rolesAside={<HelpTip id="buyer.team.roles" />}
                  sendAside={<HelpTip id="buyer.team.exclusivity" />}
                  canManage={team.data.canManage}
                  adding={add.isPending}
                  removingId={remove.isPending ? (remove.variables ?? null) : null}
                  decidingId={withdraw.isPending ? (withdraw.variables ?? null) : null}
                  settingRolesId={
                    setRoles.isPending ? (setRoles.variables?.memberId ?? null) : null
                  }
                  error={
                    errorMessage(add.error) ??
                    errorMessage(remove.error) ??
                    errorMessage(setRoles.error) ??
                    errorMessage(withdraw.error)
                  }
                  onAdd={(email, roles) => {
                    remove.reset();
                    withdraw.reset();
                    setRoles.reset();
                    return add.mutateAsync({ email, roles });
                  }}
                  onSetRoles={(memberId, roles) => {
                    add.reset();
                    remove.reset();
                    withdraw.reset();
                    setRoles.mutate({ memberId, roles });
                  }}
                  onRemove={(memberId) => {
                    add.reset();
                    withdraw.reset();
                    remove.mutate(memberId);
                  }}
                  onWithdraw={(requestId) => {
                    add.reset();
                    remove.reset();
                    withdraw.mutate(requestId);
                  }}
                />
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
